import { c as _c } from "react/compiler-runtime";
import { cn } from "@/utils/cn";
import { MapPin, X } from "lucide-react";
import React, { useCallback, useEffect } from "react";
import { Button } from "../../rac/button";
import { useAutocomplete } from "./hooks";
import type { AutocompleteItem, AutocompleteProps } from "./types";
declare global {
  interface Window {
    settlinDropdownRef?: HTMLDivElement | null;
  }
}
interface ChipAutocompleteProps extends AutocompleteProps {
  selectedItems?: AutocompleteItem[];
  onItemRemove?: (itemId: string) => void;
  showChips?: boolean;
  chipsPosition?: "above" | "below";
  filterField?: boolean;
  isScrollIntoView?: boolean;
}
export const Autocomplete: React.FC<ChipAutocompleteProps> = t0 => {
  const $ = _c(71);
  const {
    value,
    items,
    placeholder: t1,
    className: t2,
    isReady: t3,
    isValueSelected: t4,
    onInputChange,
    onItemSelect,
    onClear,
    onValueSelectedChange,
    renderItem,
    inputProps: t5,
    dropdownProps: t6,
    selectedItems: t7,
    onItemRemove,
    showChips: t8,
    chipsPosition: t9,
    filterField: t10,
    isScrollIntoView: t11,
    autoFocus: t12
  } = t0;
  const placeholder = t1 === undefined ? "Enter text" : t1;
  const className = t2 === undefined ? "" : t2;
  const isReady = t3 === undefined ? true : t3;
  const isValueSelected = t4 === undefined ? false : t4;
  let t13;
  if ($[0] !== t5) {
    t13 = t5 === undefined ? {} : t5;
    $[0] = t5;
    $[1] = t13;
  } else {
    t13 = $[1];
  }
  const inputProps = t13;
  let t14;
  if ($[2] !== t6) {
    t14 = t6 === undefined ? {} : t6;
    $[2] = t6;
    $[3] = t14;
  } else {
    t14 = $[3];
  }
  const dropdownProps = t14;
  let t15;
  if ($[4] !== t7) {
    t15 = t7 === undefined ? [] : t7;
    $[4] = t7;
    $[5] = t15;
  } else {
    t15 = $[5];
  }
  const selectedItems = t15;
  const showChips = t8 === undefined ? false : t8;
  const chipsPosition = t9 === undefined ? "above" : t9;
  const filterField = t10 === undefined ? false : t10;
  const isScrollIntoView = t11 === undefined ? true : t11;
  const autoFocus = t12 === undefined ? false : t12;
  const {
    inputRef,
    dropdownRef,
    isDropdownOpen,
    handleInputChangeWithDropdown,
    handleItemSelect,
    handleClearInput,
    handleInputFocus
  } = useAutocomplete(value, items, isReady, isValueSelected, onInputChange, onItemSelect, onClear, onValueSelectedChange);
  let t16;
  if ($[6] !== filterField || $[7] !== handleClearInput || $[8] !== handleItemSelect) {
    const handleEnhancedItemSelect = itemId => {
      handleItemSelect(itemId);
      if (filterField) {
        handleClearInput();
      }
    };
    t16 = item => <div key={item.id} onClick={() => {
      if (!item.isPlaceholder) {
        handleEnhancedItemSelect(item.id);
      }
    }} onMouseDown={_temp} className={cn("px-4 py-3 cursor-pointer", item.isPlaceholder ? "text-muted-foreground cursor-default" : "hover:bg-accent hover:text-accent-foreground")}><div className="flex flex-col"><div className="text-sm font-medium">{item.text}</div>{item.secondaryText && <div className="text-xs text-muted-foreground mt-0.5">{item.secondaryText}</div>}</div></div>;
    $[6] = filterField;
    $[7] = handleClearInput;
    $[8] = handleItemSelect;
    $[9] = t16;
  } else {
    t16 = $[9];
  }
  const defaultRenderItem = t16;
  const itemRenderer = renderItem || defaultRenderItem;
  let inputPropsOnChange;
  let restInputProps;
  let t17;
  if ($[10] !== inputProps) {
    const {
      onChange: t18,
      className: t19,
      autoFocus: _autoFocusFromInputProps,
      ...t20
    } = inputProps;
    inputPropsOnChange = t18;
    t17 = t19;
    restInputProps = t20;
    $[10] = inputProps;
    $[11] = inputPropsOnChange;
    $[12] = restInputProps;
    $[13] = t17;
  } else {
    inputPropsOnChange = $[11];
    restInputProps = $[12];
    t17 = $[13];
  }
  const inputPropsClassName = t17 === undefined ? "" : t17;
  let t18;
  let t19;
  if ($[14] !== dropdownRef || $[15] !== isDropdownOpen) {
    t18 = () => {
      if (isDropdownOpen && dropdownRef.current) {
        window.settlinDropdownRef = dropdownRef.current;
      } else {
        if (!isDropdownOpen) {
          window.settlinDropdownRef = null;
        }
      }
    };
    t19 = [isDropdownOpen, dropdownRef];
    $[14] = dropdownRef;
    $[15] = isDropdownOpen;
    $[16] = t18;
    $[17] = t19;
  } else {
    t18 = $[16];
    t19 = $[17];
  }
  useEffect(t18, t19);
  let t20;
  if ($[18] !== isScrollIntoView) {
    t20 = element => {
      if (!isScrollIntoView) {
        return;
      }
      setTimeout(() => {
        requestAnimationFrame(() => {
          const rect = element.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const elementTop = rect.top;
          if (elementTop > viewportHeight * 0.5) {
            const scrollOffset = elementTop - viewportHeight * 0.33;
            window.scrollBy({
              top: scrollOffset,
              behavior: "smooth"
            });
          } else {
            element.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest"
            });
          }
        });
      }, 150);
      setTimeout(() => {
        const rect_0 = element.getBoundingClientRect();
        const viewportHeight_0 = window.innerHeight;
        if (rect_0.bottom > viewportHeight_0 * 0.75) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest"
          });
        }
      }, 500);
    };
    $[18] = isScrollIntoView;
    $[19] = t20;
  } else {
    t20 = $[19];
  }
  const scrollInputIntoView = t20;
  let t21;
  if ($[20] !== isScrollIntoView || $[21] !== scrollInputIntoView) {
    t21 = event => {
      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || navigator.maxTouchPoints && navigator.maxTouchPoints > 2;
        if (isMobile) {
          scrollInputIntoView(target);
        } else {
          if (isScrollIntoView) {
            target.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          }
        }
      }
    };
    $[20] = isScrollIntoView;
    $[21] = scrollInputIntoView;
    $[22] = t21;
  } else {
    t21 = $[22];
  }
  const handleFocus = t21;
  let t22;
  let t23;
  if ($[23] !== handleFocus || $[24] !== inputRef) {
    t22 = () => {
      const element_0 = inputRef.current;
      if (element_0) {
        element_0.addEventListener("focus", handleFocus);
        return () => element_0.removeEventListener("focus", handleFocus);
      }
    };
    t23 = [inputRef, handleFocus];
    $[23] = handleFocus;
    $[24] = inputRef;
    $[25] = t22;
    $[26] = t23;
  } else {
    t22 = $[25];
    t23 = $[26];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[27] !== inputRef || $[28] !== scrollInputIntoView) {
    t24 = () => {
      const handleVisualViewportChange = () => {
        if (document.activeElement === inputRef.current && window.visualViewport) {
          const element_1 = inputRef.current;
          if (element_1) {
            setTimeout(() => {
              scrollInputIntoView(element_1);
            }, 100);
          }
        }
      };
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", handleVisualViewportChange);
        return () => {
          window.visualViewport?.removeEventListener("resize", handleVisualViewportChange);
        };
      }
    };
    t25 = [inputRef, scrollInputIntoView];
    $[27] = inputRef;
    $[28] = scrollInputIntoView;
    $[29] = t24;
    $[30] = t25;
  } else {
    t24 = $[29];
    t25 = $[30];
  }
  useEffect(t24, t25);
  let t26;
  if ($[31] !== chipsPosition || $[32] !== onItemRemove || $[33] !== selectedItems || $[34] !== showChips) {
    t26 = showChips && selectedItems.length > 0 ? <div className={cn("flex flex-wrap gap-2", chipsPosition === "above" ? "mb-2" : "mt-2")}>{selectedItems.map(item_0 => <div key={item_0.id} className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-gray-300 bg-white text-xs"><MapPin className="w-3 h-3 text-primary fill-primary-100" aria-hidden="true" /><span className="text-black font-normal">{item_0.text}</span>{onItemRemove && <button type="button" aria-label={`Remove ${item_0.text}`} onClick={() => onItemRemove(item_0.id)} className="ml-1 hover:bg-gray-100 rounded-full p-0.5"><X className="w-3 h-3 text-red-400" aria-hidden="true" /></button>}</div>)}</div> : null;
    $[31] = chipsPosition;
    $[32] = onItemRemove;
    $[33] = selectedItems;
    $[34] = showChips;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  const ChipsSection = t26;
  let t27;
  if ($[36] !== className) {
    t27 = cn("relative w-full h-full", className);
    $[36] = className;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  const t28 = chipsPosition === "above" && ChipsSection;
  let t29;
  if ($[38] !== handleInputChangeWithDropdown || $[39] !== inputPropsOnChange) {
    t29 = e => {
      handleInputChangeWithDropdown(e.target.value);
      if (inputPropsOnChange) {
        inputPropsOnChange(e);
      }
    };
    $[38] = handleInputChangeWithDropdown;
    $[39] = inputPropsOnChange;
    $[40] = t29;
  } else {
    t29 = $[40];
  }
  const t30 = filterField && "mt-2 rounded";
  const t31 = !filterField && "rounded-none";
  let t32;
  if ($[41] !== inputPropsClassName || $[42] !== t30 || $[43] !== t31) {
    t32 = cn("w-full h-full px-1 py-2 pr-10 bg-background text-foreground border-0 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0", t30, t31, inputPropsClassName);
    $[41] = inputPropsClassName;
    $[42] = t30;
    $[43] = t31;
    $[44] = t32;
  } else {
    t32 = $[44];
  }
  let t33;
  if ($[45] !== autoFocus || $[46] !== handleInputFocus || $[47] !== inputRef || $[48] !== placeholder || $[49] !== restInputProps || $[50] !== t29 || $[51] !== t32 || $[52] !== value) {
    t33 = <input ref={inputRef} type="text" placeholder={placeholder} value={value} onChange={t29} onFocus={handleInputFocus} autoFocus={autoFocus} className={t32} {...restInputProps} />;
    $[45] = autoFocus;
    $[46] = handleInputFocus;
    $[47] = inputRef;
    $[48] = placeholder;
    $[49] = restInputProps;
    $[50] = t29;
    $[51] = t32;
    $[52] = value;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] !== handleClearInput || $[55] !== value) {
    t34 = value && <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2 size-6 p-1" onPress={() => handleClearInput()} aria-label="Clear input"><X className="size-4 opacity-50" aria-hidden="true" /></Button>;
    $[54] = handleClearInput;
    $[55] = value;
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  const t35 = chipsPosition === "below" && ChipsSection;
  let t36;
  if ($[57] !== dropdownProps || $[58] !== dropdownRef || $[59] !== isDropdownOpen || $[60] !== isReady || $[61] !== itemRenderer || $[62] !== items) {
    t36 = isDropdownOpen && isReady && items.length > 0 && <div ref={dropdownRef} onClick={_temp2} onMouseDown={_temp3} className={cn("absolute z-[999999] bg-white border border-gray-200 shadow-lg rounded-md overflow-y-auto py-2", dropdownProps.className)} style={{
      top: "100%",
      left: "0",
      width: "100%",
      maxWidth: "90dvw",
      position: "absolute",
      zIndex: 999999,
      maxHeight: "200px",
      overflowY: "scroll",
      ...dropdownProps.style
    }} {...dropdownProps}>{items.map(item_1 => itemRenderer(item_1))}</div>;
    $[57] = dropdownProps;
    $[58] = dropdownRef;
    $[59] = isDropdownOpen;
    $[60] = isReady;
    $[61] = itemRenderer;
    $[62] = items;
    $[63] = t36;
  } else {
    t36 = $[63];
  }
  let t37;
  if ($[64] !== t27 || $[65] !== t28 || $[66] !== t33 || $[67] !== t34 || $[68] !== t35 || $[69] !== t36) {
    t37 = <div className={t27}>{t28}{t33}{t34}{t35}{t36}</div>;
    $[64] = t27;
    $[65] = t28;
    $[66] = t33;
    $[67] = t34;
    $[68] = t35;
    $[69] = t36;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  return t37;
};

// Export types and hooks for external use
export * from "./hooks";
export * from "./types";
function _temp(_e) {
  _e.preventDefault();
}
function _temp2(e_0) {
  e_0.preventDefault();
  e_0.stopPropagation();
}
function _temp3(e_1) {
  e_1.preventDefault();
  e_1.stopPropagation();
}